/**
 * R9's settings row group plus `Sign out`. Every row has a route that exists
 * today; `Help` and `About` are absent from the DOM entirely. The Followers
 * row (ISSUE-1372) carries a count pill when follow requests are waiting.
 */
import { Button } from "@sportsmet/ui";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import {
  ACCOUNT_SETTINGS_ROWS,
  followRequestBadgeLabel,
  SETTINGS_GROUP_TITLE,
  SIGN_OUT_ACTION,
  SIGN_OUT_HINT,
} from "./accountHomeCopy";

export type typeAccountSettingsGroupProps = {
  onSignOut: () => void;
  isSigningOut: boolean;
  /** How many follow requests are waiting. Zero or absent shows no pill. */
  followRequestCount?: number | undefined;
  testId?: string | undefined;
};

export const AccountSettingsGroup = (
  props: typeAccountSettingsGroupProps,
): ReactNode => (
  <section
    className="flex flex-col gap-3 rounded-lg border border-line bg-surface p-4"
    data-testid={props.testId}
  >
    <h2 className="text-h2 text-ink">{SETTINGS_GROUP_TITLE}</h2>

    <ul
      className="flex flex-col divide-y divide-line"
      data-testid="account-settings-rows"
    >
      {ACCOUNT_SETTINGS_ROWS.map((row) => (
        <li key={row.key}>
          <Link
            to={row.to}
            data-testid={`account-settings-row-${row.key}`}
            className="flex min-h-touch-target items-center gap-2 py-3 text-body text-ink hover:underline"
          >
            {row.label}
            {row.key === "followers" && (props.followRequestCount ?? 0) > 0 ? (
              <span
                className="grid min-w-6 place-items-center rounded-full bg-primary-solid px-2 text-caption text-on-primary"
                aria-label={followRequestBadgeLabel(
                  props.followRequestCount ?? 0,
                )}
                data-testid="account-followers-badge"
              >
                {props.followRequestCount}
              </span>
            ) : null}
          </Link>
        </li>
      ))}
    </ul>

    <div className="flex flex-col items-start gap-1 pt-2">
      <Button
        variant="secondary"
        loading={props.isSigningOut}
        loadingLabel="Signing out"
        onClick={props.onSignOut}
        data-testid="account-sign-out"
      >
        {SIGN_OUT_ACTION}
      </Button>
      <p className="text-caption text-ink-secondary">{SIGN_OUT_HINT}</p>
    </div>
  </section>
);
